/**
 * Reusable wrapper for landing page sections that supply their own container.
 *
 * What: `LandingSectionShell` plus a `Container`, with the section's own vertical padding.
 * Why: Sections that aren't already nested inside a page-level container need one;
 * the section rhythm itself (scroll margin, tone, reveal hook) lives in the shell,
 * so there is exactly one source of it.
 * When: Update when changing landing container widths or section padding.
 */

import type { ElementType, ReactNode } from 'react';
import { Container } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { Breakpoint } from '@mui/system';

import type { LandingSectionTone } from '@/components/landing/LandingSectionShell';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { sectionSpacing } from '@/components/landing/landingTokens';

interface LandingSectionProps {
  id: string;
  children: ReactNode;
  as?: ElementType;
  maxWidth?: Breakpoint | false;
  disableGutters?: boolean;
  py?: number | string | Record<string, number | string>;
  tone?: LandingSectionTone;
  ariaLabelledby?: string;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
}

function LandingSection({
  id,
  children,
  as = 'section',
  maxWidth = 'lg',
  disableGutters = true,
  py,
  tone = 'transparent',
  ariaLabelledby,
  sx,
  containerSx,
}: LandingSectionProps) {
  return (
    <LandingSectionShell
      id={id}
      as={as}
      ariaLabelledby={ariaLabelledby}
      tone={tone}
      // Spacing between these sections comes from the page-level stack.
      mb={0}
      sx={{
        py: py || sectionSpacing.py,
        ...sx,
      }}
    >
      <Container
        maxWidth={maxWidth}
        disableGutters={disableGutters}
        sx={containerSx}
      >
        {children}
      </Container>
    </LandingSectionShell>
  );
}

export default LandingSection;
